import { type Ref, ref } from 'vue'
import { api } from '@/api'

/** A task a successor can start: picked by its name, stored by its slug. */
export type SuccessorTaskChoice = { slug: string; name: string }

/** As many of the enterprise's tasks as the picker reads in one request, the size the other task pickers read. */
const TASK_LIMIT = 200

/** A read that may fail, or may be missing where a screen is shown without it: either way it answers nothing. */
async function quietly<T>(read: () => Promise<T>): Promise<T | null> {
    try {
        return await read()
    } catch {
        return null
    }
}

/**
 * What the task screens' successors editor offers (#1754): the enterprise's tasks, by name, and the outcomes a
 * successor may wait on, as the API serves them. The portal holds no copy of the outcomes, so the picker cannot drift
 * from what the successor validator accepts. A read that fails leaves its list empty: the editor still shows every
 * stored edge as it is, and the JSON toggle still writes any edge.
 */
export function useSuccessorChoices(): {
    tasks: Ref<SuccessorTaskChoice[]>
    outcomes: Ref<string[]>
    load: () => Promise<void>
} {
    const tasks = ref<SuccessorTaskChoice[]>([])
    const outcomes = ref<string[]>([])

    /** Each list fills as soon as its own read answers, so a slow task list never holds the outcomes back. */
    async function loadTasks(): Promise<void> {
        const listed = await quietly(() => api.tasks({ limit: TASK_LIMIT }))
        tasks.value = (listed?.data ?? [])
            .map(task => ({ slug: task.slug, name: task.name || task.slug }))
            .sort((left, right) => left.name.localeCompare(right.name))
    }

    async function loadOutcomes(): Promise<void> {
        const served = await quietly(() => api.successorOutcomes())
        outcomes.value = Array.isArray(served) ? served : []
    }

    async function load(): Promise<void> {
        await Promise.all([loadTasks(), loadOutcomes()])
    }

    return { tasks, outcomes, load }
}
